/**
 * client/src/app/aal3-route-status.ts
 *
 * What the AAL3 gate panel needs in order to tell someone the TRUTH about why
 * they are still looking at it.
 *
 * The reported defect: a person enrols a security key, signs back in, and gets
 * the Level 3, fresh gate again — showing the same "set up a security key"
 * prompt they believe they already followed. Two facts were missing from every
 * surface involved:
 *
 *  1. WHICH of their enrolled keys (if any) can reach Level 3, fresh. Only
 *     a credential whose FIDO MDS attestation chain verified at registration
 *     qualifies (`server/logic/webauthn/aal-cap.ts` at login, `step-up.ts` for
 *     step-up) — a key enrolled in the `device` or `external_standard` mode
 *     never can, and looked identical everywhere in the product.
 *  2. That holding a qualifying key is NOT sufficient: the session AAL is
 *     capped by what THIS sign-in proved (the login high-water-mark cap), so
 *     signing in with a platform passkey while a certified key sits unused on
 *     the account lands exactly here, every time.
 *
 * Honesty rules this hook exists to keep (Friction Rule clause 2):
 *  - Both signals are three-valued. An unresolved or failed read is `unknown`,
 *    never a "no" — the panel must not tell someone their key doesn't qualify
 *    because a fetch failed.
 *  - The cognitive route reports whether it can CURRENTLY grant Level 3,
 *    fresh, which is a different question from whether a bank meets the
 *    baseline. It is now permanently `"unavailable"`: the server-side
 *    promotion path is retired outright (`LEGACY_COGNITIVE_PROMOTION_DISABLED`,
 *    server/logic/seed-interview/reconfirm.ts, 2026-08-31 security tier ladder
 *    spec module 06 R8), so offering the memory bank as an equivalent way out
 *    of this screen would be advice that cannot work.
 *
 * Neither value gates anything. Every real decision is re-made server-side.
 */

import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  resolveHardwareRoute,
  offerableCredentials,
  isMasterHeldWithSurvivor,
  type HardwareRoute,
} from "@shared/lib/security-posture/hardware-route";
import { resolveCognitiveRoute, type CognitiveRoute } from "./cognitive-route";

/**
 * `HardwareRoute` and its derivation now live in `shared/` (AC-26) — a later
 * server-side reader needs the identical logic over the server's own
 * credential rows, so re-typing either one here would be exactly the drift
 * `shared/` exists to prevent. See `shared/lib/security-posture/
 * hardware-route.ts` for the type, the honesty rules behind each state, and
 * `resolveHardwareRoute` itself. Re-exported so this file stays the existing
 * import path for the type (`protection-level-copy.ts`).
 */
export type { HardwareRoute };

/**
 * Whether the dual-passkey basis (server/logic/webauthn/step-up-dual.ts) can
 * currently reach Level 3, fresh — TWO distinct, each ≥24h-mature active
 * passkeys. Display-only: the 24h bound below mirrors, but is never read by,
 * the server's own DUAL_LEG_MIN_CREDENTIAL_AGE_MS floor — the server always
 * re-derives maturity itself at ceremony time (step-up-dual-eligibility.ts).
 */
export type TwoKeyRoute =
  /** Not readable right now — say nothing either way. */
  | "unknown"
  /** Fewer than two active passkeys — no second device to check against. */
  | "needs-second-device"
  /** ≥2 active passkeys, but fewer than 2 are known to be ≥24h old yet. */
  | "waiting"
  /** ≥2 active passkeys are each ≥24h old — the ceremony can run now. */
  | "available"
  /** #3268 review round 1 MAJOR 2 — mirrors `HardwareRoute`'s `"master-held"`:
   *  the designated master is incident-held and excluded by the offerable
   *  filter (an unheld key survives). `step-up/begin.ts` is master-pinned for
   *  BOTH bases, so the dual ceremony cannot complete either — checked before
   *  every other verdict. */
  | "master-held";

/** Mirrors DUAL_LEG_MIN_CREDENTIAL_AGE_MS (server/logic/webauthn/
 *  step-up-dual-eligibility.ts) for DISPLAY ONLY — never an enforcement
 *  input; the server re-derives maturity itself at every ceremony step. */
const TWO_KEY_MIN_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The ≥24h maturity predicate, ONE copy shared by every resolver below (the
 * client mirror of the server's `isCredentialMature`,
 * step-up-dual-eligibility.ts — display only). A row with no readable
 * `createdAt` is NOT yet known-mature (the cautious direction for a display
 * that exists to say a ceremony will actually run), never treated as unknown
 * overall. Kept in one place so the 24h-floor / missing-timestamp / NaN rules
 * cannot drift between the three routes that each depend on them.
 */
function isMatureCredential(c: CredentialCertification, now: Date): boolean {
  if (!c.createdAt) return false;
  const addedAt = new Date(c.createdAt).getTime();
  if (Number.isNaN(addedAt)) return false;
  return now.getTime() - addedAt >= TWO_KEY_MIN_AGE_MS;
}

// `CognitiveRoute`/`resolveCognitiveRoute` moved to `./cognitive-route` for
// the 300-line cap (review round 1) — re-exported below unchanged.
export type { CognitiveRoute } from "./cognitive-route";
export { resolveCognitiveRoute } from "./cognitive-route";

/** The subset of `GET /api/auth/webauthn/credentials` this needs. */
interface CredentialCertification {
  revoked?:   boolean;
  /** Server-derived `mds_verified`. Absent on a server predating the
   *  projection — treated as unreadable, never as `false`. */
  certified?: boolean;
  /** ISO timestamp — when this credential was enrolled (drives TwoKeyRoute). */
  createdAt?: string;
  /** Server-projected: this credential is the account's designated master
   *  (ledger #86). Display-only, like every field here — the server resolves
   *  the active master from its own rows at every ceremony step. */
  isMaster?: boolean;
  /**
   * Server-projected: this credential is still queued in the deferred FIDO MDS
   * re-verification queue (`server/logic/webauthn/credentials.ts` →
   * `listCredentials`). True means its certification has NOT been established
   * either way — the registry was unreachable when it was enrolled and the
   * retained evidence is still waiting on the sweep.
   */
  pendingReverification?: boolean;
  /**
   * Held after a closed incident (AC-8, #3099; `incident_held_at`). #3268: a
   * held key is refused at every real ceremony
   * (`server/logic/webauthn/step-up/begin.ts`, `offerableForCeremony`) while an
   * unheld key survives, so a route computed here must not offer one no
   * ceremony will actually accept. Absent reads as not held.
   */
  incidentHeld?: boolean;
}

export interface Aal3RouteStatus {
  hardware:  HardwareRoute;
  cognitive: CognitiveRoute;
  twoKey:    TwoKeyRoute;
  /**
   * When the two-key route flips from "waiting" to "available", if it isn't
   * already. Undefined whenever that can't be said honestly (already
   * available, no second device, or maturity isn't computable from the data
   * on hand) — a display MUST NOT guess a time it doesn't have (Friction Rule
   * clause 2, same three-valued posture as every other field on this type).
   */
  twoKeyMatureAt: Date | undefined;
  /**
   * Whether the two-passkey ceremony can complete WITHOUT the certified key
   * (ledger #86) — the fact the protection card's secondary "use two of your
   * passkeys instead" offer needs, which `twoKey` alone cannot answer:
   * `twoKey === "available"` counts the certified key itself, and when the
   * master IS the certified key the promoting leg must land on it. `true`
   * only on an affirmative read; `undefined` while unreadable (never shown).
   */
  twoKeyWithoutCertified: boolean | undefined;
}

/** Read all three routes' current state. `enabled` gates the fetches to the AAL3 gate. */
export function useAal3RouteStatus(enabled: boolean): Aal3RouteStatus {
  // Same query key the Security Centre and the backup-passkey nudge already
  // use, so this is usually a cache read rather than a fetch.
  const { data: credentials, isSuccess: credentialsRead } = useQuery<CredentialCertification[]>({
    queryKey: ["webauthn/credentials"],
    queryFn:  () => apiRequest<CredentialCertification[]>("/api/auth/webauthn/credentials"),
    enabled,
    retry:    false,
  });

  // The seeding-progress fetch this used to make is GONE: the cognitive route
  // is permanently "unavailable" (see `resolveCognitiveRoute`), so reading a
  // bank's baseline state here would be a network round-trip whose answer
  // cannot change any copy on this screen.

  const resolvedCredentials = credentialsRead ? credentials : undefined;

  return {
    hardware:               resolveHardwareRoute(resolvedCredentials),
    cognitive:              resolveCognitiveRoute(),
    twoKey:                 resolveTwoKeyRoute(resolvedCredentials),
    twoKeyMatureAt:         resolveTwoKeyMatureAt(resolvedCredentials),
    twoKeyWithoutCertified: resolveTwoKeyWithoutCertified(resolvedCredentials),
  };
}

/**
 * Pure counterpart for the dual-passkey route. `undefined` ⇒ `"unknown"`,
 * matching the other two resolvers. A credential with no readable `createdAt`
 * is treated as NOT yet known-mature (the safe direction on a display that
 * exists to tell someone a ceremony will actually work) — never as "unknown
 * overall", since an account can legitimately mix known- and unknown-age rows.
 */
export function resolveTwoKeyRoute(
  credentials: CredentialCertification[] | undefined,
  now: Date = new Date(),
): TwoKeyRoute {
  if (credentials === undefined) return "unknown";
  const unrevoked = credentials.filter((c) => c.revoked !== true);
  // #3268 review round 1 MAJOR 2: checked BEFORE the offerable filter/2-device
  // check — a held master blocks the dual ceremony too (begin.ts is
  // master-pinned for both bases).
  if (isMasterHeldWithSurvivor(unrevoked)) return "master-held";
  // #3268: judge the hold over the FULL unrevoked set, before either the
  // 2-device or the maturity check — mirrors `step-up/begin.ts`'s
  // `offerableForCeremony(credentials.filter(c => !c.revokedAt))`.
  const active = offerableCredentials(unrevoked);
  if (active.length < 2) return "needs-second-device";

  const mature = active.filter((c) => isMatureCredential(c, now));

  return mature.length >= 2 ? "available" : "waiting";
}

/**
 * When the two-key route becomes available, if it isn't already — the
 * earliest moment at which 2 DISTINCT active credentials are both ≥24h old.
 * That moment is exactly when the 2nd-oldest active credential crosses the
 * maturity floor (any older credential is already past it, or will cross
 * earlier). Returns `undefined` for every case a display must not guess:
 * already available, fewer than 2 active credentials, or maturity isn't
 * computable (missing/invalid `createdAt` on enough rows to be sure).
 */
export function resolveTwoKeyMatureAt(
  credentials: CredentialCertification[] | undefined,
  now: Date = new Date(),
): Date | undefined {
  if (credentials === undefined) return undefined;
  const unrevoked = credentials.filter((c) => c.revoked !== true);
  // #3268 review round 1 MAJOR 2: a held master blocks the dual ceremony
  // outright — no maturity time is honest to promise.
  if (isMasterHeldWithSurvivor(unrevoked)) return undefined;
  // #3268: same offerable-set mirror as resolveTwoKeyRoute above.
  const active = offerableCredentials(unrevoked);
  if (active.length < 2) return undefined;

  const ages = active
    .map((c) => (c.createdAt ? new Date(c.createdAt).getTime() : NaN))
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b);
  if (ages.length < 2) return undefined;

  const matureAtMs = ages[1] + TWO_KEY_MIN_AGE_MS;
  return matureAtMs > now.getTime() ? new Date(matureAtMs) : undefined;
}

/**
 * Can the two-passkey ceremony complete WITHOUT the certified key? (ledger
 * #86.) The secondary "use two of your passkeys instead" offer must never
 * promise key-avoidance it cannot deliver, so this resolver mirrors the
 * server's leg rules (step-up-dual-eligibility.ts) for DISPLAY ONLY:
 *
 *  - No master: both legs draw from any mature credential, so ≥2 mature
 *    NON-certified credentials are needed.
 *  - Master designated: the promoting leg MUST be the master
 *    (resolveDualLeg2Offered), so the master must itself be a mature
 *    non-certified credential, with ≥1 other mature non-certified credential
 *    left for leg 1. A certified master ⇒ `false`, unconditionally.
 *
 * `undefined` in ⇒ `undefined` out (unreadable — the offer must not render).
 * A row with `certified` unreadable is treated as NOT safely usable — the
 * cautious direction for an offer whose label says "no key with you?".
 */
export function resolveTwoKeyWithoutCertified(
  credentials: CredentialCertification[] | undefined,
  now: Date = new Date(),
): boolean | undefined {
  if (credentials === undefined) return undefined;
  const unrevoked = credentials.filter((c) => c.revoked !== true);
  // #3268 review round 1 MAJOR 2 (was the reported bug): a held master used to
  // fall out of `active` below and silently read as "no master", offering a
  // key-avoiding leg the ceremony would refuse outright. Checked first now.
  if (isMasterHeldWithSurvivor(unrevoked)) return false;
  // #3268: same offerable-set mirror as resolveTwoKeyRoute above.
  const active = offerableCredentials(unrevoked);
  const matureNonCertified = active.filter(
    (c) => c.certified === false && isMatureCredential(c, now),
  );
  const master = active.find((c) => c.isMaster === true);
  if (master) {
    return master.certified === false && matureNonCertified.includes(master)
      && matureNonCertified.length >= 2;
  }
  return matureNonCertified.length >= 2;
}
